// What the workspace sends across a grant, for the panels and settings that
// declare `sends` (02-DESIGN/14-solutions/03-the-console.md § the elements:
// a panel or a setting drawn only where the workspace sends; hq issue 185).
// The workspace's grants are lines of its own blueprint, read as Settings
// reads it for *Managed by …*: each a capability, the receiving workspace
// and, optionally, the solution it is for. A workspace with no blueprint sends nothing; a read refused or
// unanswered draws nothing that declares `sends`, rather than a panel that
// may be wrong. The read is an admin's: a member is drawn nothing that
// declares `sends`, which is what the broker's refusal would give.

import { useMemo } from "react";

import type { Bus } from "../../platform/bus.ts";
import { useLoad } from "../../ui/components.tsx";
import { SERVICE as BLUEPRINT, Subjects as BlueprintSubjects, type Entry } from "../../wire/blueprint.ts";
import type { Capability } from "../../wire/solutions.ts";

/** Grant is one grant line of the workspace's own blueprint. */
export interface Grant {
  capability: string;
  receiver: string;
  solution?: string;
}

/** grantsOf reads the grant lines of a blueprint document; anything unreadable is no grant. */
export function grantsOf(document: string | undefined): Grant[] {
  if (document === undefined || document === "") {
    return [];
  }
  let doc: unknown;
  try {
    doc = JSON.parse(document);
  } catch {
    return [];
  }
  if (typeof doc !== "object" || doc === null || !("grants" in doc) || !Array.isArray(doc.grants)) {
    return [];
  }
  const out: Grant[] = [];
  for (const g of doc.grants as unknown[]) {
    if (typeof g !== "object" || g === null) {
      continue;
    }
    const { capability, receiver, solution } = g as Record<string, unknown>;
    if (typeof capability === "string" && typeof receiver === "string" && receiver !== "") {
      out.push({
        capability,
        receiver,
        ...(typeof solution === "string" && solution !== "" ? { solution } : {}),
      });
    }
  }
  return out;
}

/** forSolution is the grant lines that apply to the solution: those naming it, and those naming none. */
export function forSolution(grants: readonly Grant[], solution: string): Grant[] {
  return grants.filter((g) => g.solution === undefined || g.solution === solution);
}

/**
 * sendsHere reports whether something declaring `sends` is drawn: always
 * where it declares none; otherwise only where a grant line sends one of the
 * capabilities it names.
 */
export function sendsHere(sends: readonly Capability[] | undefined, grants: readonly Grant[]): boolean {
  if (sends === undefined || sends.length === 0) {
    return true;
  }
  return grants.some((g) => (sends as readonly string[]).includes(g.capability));
}

const WORDS: Readonly<Record<string, string>> = {
  audit: "records go to",
  usage: "usage goes to",
};

/**
 * grantWords is the grants a panel was drawn for, in the words Settings uses:
 * *records go to northwind*, *usage goes to northwind* — one line per grant
 * that sends a capability the panel names, in the blueprint's order.
 */
export function grantWords(sends: readonly Capability[] | undefined, grants: readonly Grant[]): string[] {
  if (sends === undefined || sends.length === 0) {
    return [];
  }
  return grants
    .filter((g) => (sends as readonly string[]).includes(g.capability))
    .map((g) => `${WORDS[g.capability] ?? `${g.capability} goes to`} ${g.receiver}`);
}

/**
 * useGrants is the workspace's grant lines, read on every visit for an
 * admin; nothing for a member, while the read is pending, and where it is
 * refused, unanswered or the workspace has no blueprint.
 */
export function useGrants(bus: Bus, admin: boolean): readonly Grant[] {
  return useGrantsRead(bus, admin).grants;
}

/**
 * useGrantsRead is useGrants with whether the read has answered: a page that
 * decides from the grants — a panel that exists only where a grant sends —
 * waits for `ready` rather than judging on the empty set it starts with. A
 * read refused has answered, with no grants.
 */
export function useGrantsRead(bus: Bus, admin: boolean): { grants: readonly Grant[]; ready: boolean } {
  const [own] = useLoad(`own-blueprint/grants/${String(admin)}`, () =>
    admin ? bus.call<Entry>(BLUEPRINT, BlueprintSubjects.ownGet(), undefined) : Promise.resolve(undefined),
  );
  const document = own.state === "ready" ? own.value?.document : undefined;
  const grants = useMemo(() => (document === undefined ? NONE : grantsOf(document)), [document]);
  return { grants, ready: own.state !== "pending" };
}

const NONE: readonly Grant[] = [];
